import { useCallback } from 'react';

import { SEARCH_CACHE_SECONDS, SEARCH_LOCATION_CITY_FILTER } from '#src/app/pages/search/shared/consts';

import { useSearchClient } from '../../../../../useSearchClient';
import { useSearchIndexName } from '../../../../../useSearchIndexName';

// the suggestions wait for the counts, so a slow request falls back to showing them unmerged
const COUNTS_TIMEOUT_MS = 300;

const TIMED_OUT = Symbol('timed out');

/**
 * Resolves with TIMED_OUT after `ms`, so a slow request can be raced without being cancelled
 */
async function timeoutAfter(ms: number): Promise<typeof TIMED_OUT> {
	// oxlint-disable-next-line promise/avoid-new -- wrapping setTimeout is the one place a new promise is needed
	return new Promise((resolve) => {
		setTimeout(() => resolve(TIMED_OUT), ms);
	});
}

type PetitionCounts = Readonly<Record<string, number>>;

async function reportLateCounts(request: Promise<PetitionCounts | undefined>, onLateCounts?: () => void) {
	try {
		if (await request) onLateCounts?.();
	} catch {
		// the suggestions already showed unmerged, which is all a failure would do
	}
}

type Result = ModelHookResult<
	undefined,
	{
		/**
		 * Resolves to undefined when the counts can't be fetched in time, so callers can skip count-based merging.
		 * If the request is only slow, `onLateCounts` runs once it lands (and is cached), so callers can fetch again
		 */
		getCityPetitionCounts: (
			cityStates: readonly string[],
			onLateCounts?: () => void,
		) => Promise<PetitionCounts | undefined>;
	}
>;

export function useCityPetitionCounts(): Result {
	const {
		data: { typesenseClient },
	} = useSearchClient();

	const {
		data: { searchIndexName },
	} = useSearchIndexName();

	const fetchCounts = useCallback(
		async (cityStates: readonly string[]) => {
			// values come from the locations collection; backticks would end the filter value early
			const values = cityStates.map((value) => `\`${value.replace(/`/g, '')}\``).join(',');
			const response = await typesenseClient
				.collections(searchIndexName)
				.documents()
				.search(
					{
						q: '*',
						query_by: '*',
						filter_by: `${SEARCH_LOCATION_CITY_FILTER}:=[${values}]`,
						facet_by: SEARCH_LOCATION_CITY_FILTER,
						max_facet_values: cityStates.length,
						per_page: 0,
					},
					{
						cacheSearchResultsForSeconds: SEARCH_CACHE_SECONDS,
					},
				);
			const facetCount = response.facet_counts?.find((facet) => facet.field_name === SEARCH_LOCATION_CITY_FILTER);
			if (!facetCount?.counts) return undefined;
			return Object.fromEntries(facetCount.counts.map(({ value, count }) => [value, count]));
		},
		[searchIndexName, typesenseClient],
	);

	return {
		actions: {
			getCityPetitionCounts: useCallback(
				async (cityStates: readonly string[], onLateCounts?: () => void) => {
					if (!cityStates.length) return undefined;
					const request = fetchCounts(cityStates);
					try {
						const result = await Promise.race([request, timeoutAfter(COUNTS_TIMEOUT_MS)]);
						if (result !== TIMED_OUT) return result;
					} catch {
						return undefined;
					}
					void reportLateCounts(request, onLateCounts);
					return undefined;
				},
				[fetchCounts],
			),
		},
	};
}
